import { Debounce, Watch } from '@Ravnur-Inc/decorators';

import moment, { Duration, unitOfTime } from 'moment';
import { prop, Vue, Options } from 'vue-class-component';

import { $Props } from '../../typings/tsx';
import MaskedInput from '../masked-input/masked-input';

import './time-code-input.scss';

const MAX = 9999999;
const MIN = 0;

const DEFAULT_STEP = 100;
const DEFAULT_FORMAT = 'HH:mm:ss';
const CN = 'time-code-input';

const SUPPORTED_TOKENS = ['H', 'h', 'k', 'm', 's', 'S'];

class Props {
  value = prop<Nullable<number>>({ default: null });
  format = prop({ default: DEFAULT_FORMAT });
  label = prop({ default: '' });
  disabled = prop({ default: false });
  step = prop({ default: DEFAULT_STEP });
  stepUnit = prop<unitOfTime.Base>({ default: 'millisecond' });
  showSwitcher = prop({ default: true });
  placeholder = prop({ default: '' });
  hasErrors = prop({ default: false });

  max = prop({ default: MAX });
  min = prop({ default: MIN });
  unit = prop<unitOfTime.Base>({ default: 'second' });
}

type Emits = {
  onFocus: () => void;
};

@Options({
  inheritAttrs: false,
  name: 'time-code-input',
  emits: ['focus', 'update:value'],
})
export default class TimeCodeInput extends Vue.with(Props) {
  declare $props: $Props<Props, Emits, 'value'>;
  private time: Duration | null = null;

  private get mask(): string {
    return SUPPORTED_TOKENS.reduce((memo, token) => memo.split(token).join('1'), this.format);
  }

  private get asString(): string {
    const { time } = this;
    return (time as any)?.format(this.format, { trim: false }) || '';
  }

  private get isMax(): boolean {
    return this.max === this.value;
  }

  private get isMin(): boolean {
    return this.min === this.value;
  }

  @Watch('value', { immediate: true })
  private refreshTime() {
    // eslint-disable-next-line import/no-named-as-default-member
    this.time = typeof this.value === 'number' ? moment.duration(this.value, this.unit) : null;
  }

  render() {
    const cn = {
      [CN]: true,
      [`${CN}--compact`]: !this.showSwitcher,
    };
    return (
      <div class={cn}>
        {this.renderDecreaseButton()}
        <MaskedInput
          ref="input"
          class={`${CN}__mask`}
          disabled={this.disabled}
          label={this.label}
          mask={this.mask}
          placeholder={this.placeholder}
          value={this.asString}
          // hasErrors={hasErrors}
          onBlur={this.onBlurInput}
          onFocus={this.handleFocusEvent}
        />
        {this.renderIncreaseButton()}
      </div>
    );
  }

  private renderDecreaseButton() {
    if (!this.showSwitcher) {
      return;
    }
    return (
      <r-button
        class={[`${CN}__btn`, `${CN}__btn--left`]}
        color="primary"
        disabled={this.isMin || this.disabled}
        icon="arrow-left"
        onclick={this.decrease}
      />
    );
  }

  renderIncreaseButton() {
    if (!this.showSwitcher) {
      return;
    }
    return (
      <r-button
        class={[`${CN}__btn`, `${CN}__btn--right`]}
        color="primary"
        disabled={this.isMax || this.disabled}
        icon="arrow-right"
        onclick={this.increase}
      />
    );
  }

  private increase() {
    this.time?.add(this.step, this.stepUnit);
    this.emitTime();
  }

  private decrease() {
    this.time?.subtract(this.step, this.stepUnit);
    this.emitTime();
  }

  @Watch('min')
  @Watch('max')
  @Debounce(0)
  private emitTime() {
    const { time, unit } = this;
    if (time) {
      this._normalizeAndEmit(time.as(unit));
    }
  }

  private onBlurInput(value: string) {
    const val = value.split('_').join('0');
    const m = moment(val, this.format);
    const d = {
      milliseconds: m.milliseconds(),
      seconds: m.seconds(),
      minutes: m.minutes(),
      hours: m.hours(),
      days: 0,
      weeks: 0,
      months: 0,
      years: 0,
    };
    // eslint-disable-next-line import/no-named-as-default-member
    const time = moment.duration(d).as(this.unit);
    this._normalizeAndEmit(time);
    const input = this.$refs.input as MaskedInput;
    input.changeValue();
  }

  private _normalizeAndEmit(time: number) {
    time = Math.max(time, this.min);
    time = Math.min(time, this.max);
    // eslint-disable-next-line import/no-named-as-default-member
    this.time = moment.duration(time, this.unit);
    this.$emit('update:value', this.time.as(this.unit));
  }

  private handleFocusEvent() {
    this.$emit('focus');
  }
}
